import { useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const guardKey = '__fanbirdLiveExitGuard';
type HistoryState = Record<string, any> | null;
type Guard = { id: string; url: string; base: HistoryState; index: number | null; hasPrevious: boolean };
type Options = { enabled: boolean; onExitRequested: () => void };

const indexOf = (state: HistoryState): number | null => Number.isInteger(state?.idx) ? state!.idx : null;

function createGuard(onExitRequested: () => void) {
  let guard: Guard | null = null;
  let enabled = false;
  let requested = false;
  let backDelta: number | null = null;
  let phase: 'armed' | 'restoring' | 'releasing' = 'armed';
  let afterRestore: (() => void) | null = null;
  let afterRelease: (() => void) | null = null;
  let unknownSteps = 0;
  let departure: { url: string; state: HistoryState } | null = null;

  const isGuard = (state: HistoryState) => !!guard && state?.[guardKey]?.id === guard.id;
  const notify = () => {
    if (!enabled || requested) return;
    requested = true;
    onExitRequested();
  };
  const pushGuard = () => {
    if (!guard) return;
    window.history.pushState({ ...guard.base, ...(guard.index === null ? {} : { idx: guard.index + 1 }), [guardKey]: guard }, '', guard.url);
  };
  const detach = () => {
    window.removeEventListener('popstate', onPop, true);
    guard = null;
    requested = false;
    backDelta = null;
    afterRestore = null;
    departure = null;
  };
  const finishRelease = () => {
    if (!guard) return;
    const state = departure && guard.index !== null ? { ...departure.state, idx: guard.index } : departure?.state ?? guard.base;
    window.history.replaceState(state, '', departure ? departure.url : guard.url);
    const action = departure ? null : afterRelease;
    if (departure) enabled = false;
    afterRelease = null;
    detach();
    action?.();
    if (enabled) arm();
  };
  const restored = () => {
    phase = 'armed';
    const action = afterRestore;
    afterRestore = null;
    if (action) action();
    else if (backDelta !== null) notify();
  };

  function onPop(event: PopStateEvent) {
    if (!guard) return;
    // BrowserRouter listens in the bubble phase. Keep it on the live route while
    // restoring history so the player and the payment prompt remain mounted.
    event.stopImmediatePropagation();
    const currentIndex = indexOf(event.state);
    if (phase === 'releasing') {
      if (window.location.href === guard.url && currentIndex === guard.index) finishRelease();
      else window.history.go(guard.index !== null && currentIndex !== null ? guard.index - currentIndex : 1);
      return;
    }
    if (phase === 'restoring') {
      if (isGuard(event.state)) { restored(); return; }
      // Entries created before BrowserRouter may have no index. Walk forward
      // until the known base entry, without notifying the router on the way.
      if (window.location.href === guard.url && currentIndex === guard.index) {
        if (unknownSteps) backDelta = -(unknownSteps + 1);
        pushGuard(); restored();
      } else {
        unknownSteps += 1;
        window.history.go(1);
      }
      return;
    }
    if (isGuard(event.state)) return;
    if (window.location.href === guard.url && currentIndex === guard.index) {
      // A normal back/swipe consumes our duplicate, rather than leaving the page.
      // Put it back immediately; confirming later skips both live entries.
      if (backDelta === null) backDelta = -2;
      pushGuard(); notify();
    } else {
      phase = 'restoring';
      if (guard.index !== null && currentIndex !== null) {
        if (backDelta === null) backDelta = currentIndex - (guard.index + 1);
        window.history.go(guard.index + 1 - currentIndex);
      } else {
        unknownSteps = 1;
        window.history.go(1);
      }
    }
  }

  const release = (action: () => void) => {
    if (phase === 'releasing') {
      // A redirect may replace/push the current entry while our back() is still
      // queued. Preserve that destination when the pending pop arrives.
      if (guard && !isGuard(window.history.state)) departure = { url: window.location.href, state: window.history.state };
      return;
    }
    if (afterRestore) return;
    if (!guard) { action(); return; }
    if (phase === 'restoring') { afterRestore = () => release(action); return; }
    // A different route can replace/push history before React runs cleanup.
    // Never move that route backwards on behalf of an unmounted live screen.
    if (!isGuard(window.history.state)) { detach(); action(); return; }
    phase = 'releasing';
    afterRelease = action;
    window.history.back();
  };

  function arm() {
    if (guard) return;
    phase = 'armed';
    const state = window.history.state as HistoryState;
    // Reuse a surviving entry when the user returns to the live route.
    const existing = state?.[guardKey] as Guard | undefined;
    guard = existing?.url === window.location.href ? existing : {
      id: `${Date.now()}-${Math.random()}`, url: window.location.href, base: state, index: indexOf(state), hasPrevious: window.history.length > 1,
    };
    window.addEventListener('popstate', onPop, true);
    if (!existing || existing.url !== window.location.href) pushGuard();
  }

  return {
    enable() {
      enabled = true;
      // Router navigation can reuse the live screen for another broadcast.
      // Keep the previous entry intact and guard the newly committed route.
      if (guard && phase === 'armed' && !isGuard(window.history.state)) detach();
      arm();
    },
    disable() {
      enabled = false;
      requested = false;
      backDelta = null;
      release(() => {});
    },
    requestExit() { if (phase === 'armed') notify(); },
    cancelExit() { requested = false; backDelta = null; },
    confirmExit(fallback: () => void) {
      const delta = backDelta;
      const hasPrevious = guard?.hasPrevious !== false;
      enabled = false;
      release(delta === null || !hasPrevious ? fallback : () => window.history.go(delta + 1));
    },
    navigateAway(action: () => void) {
      enabled = false;
      release(action);
    },
  };
}

/** Guard browser back/swipe as well as an explicit close, without a data router. */
export default function useLiveExitGuard({ enabled, onExitRequested }: Options) {
  const location = useLocation();
  const callback = useRef(onExitRequested);
  callback.current = onExitRequested;
  const controller = useRef<ReturnType<typeof createGuard> | null>(null);
  if (!controller.current) controller.current = createGuard(() => callback.current());
  const revision = useRef(0);
  useLayoutEffect(() => {
    const current = ++revision.current;
    const guard = controller.current!;
    if (enabled) guard.enable(); else guard.disable();
    return () => {
      // StrictMode immediately runs setup again. Do not pop the entry between
      // those two setups, or create a second sentinel during its replay.
      void Promise.resolve().then(() => { if (revision.current === current) guard.disable(); });
    };
  }, [enabled, location.key]);
  return controller.current;
}
